import { DATE_PIPE_DEFAULT_OPTIONS, DecimalPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Routes, withComponentInputBinding } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { DateTransformInterceptor } from '@helpers/date-transform.interceptor';
import { ChunkLoadErrorHandler } from '@helpers/chunk-load-error.handler';
import { JwtInterceptor } from '@helpers/jwt.interceptor';
import { RefreshTokenInterceptor } from '@helpers/refresh-token.interceptor';
import { provideTranslateService } from '@ngx-translate/core';
import Aura from '@primeng/themes/aura';
import { AccessVerifierGuard } from '@shared/guards/access-verifier.guard';
import { FeatureFlagsService } from '@shared/services/feature-flags.service';
import { TextsService } from '@shared/services/texts.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { routes } from './app.routes';

const locale = ([] as { code: string; culture: string }[]).find(x => x.code.startsWith(navigator.language))?.culture ?? 'en-GB';

registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeEnGb, 'en-GB');

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: environment.azureAd.clientId,
    authority: environment.azureAd.authority,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
});

/**
 * Factory function to create MsalGuardConfiguration
 * Note: We don't use state parameter because long URLs cause AADSTS90015 error
 * Instead, we store the return URL in localStorage (see auth.service.ts)
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/error/401',
  };
}

// Added Route Validations for AccessVerifierGuard
validateRoutes(routes);

export const appConfig: ApplicationConfig = {
  providers: [
    MessageService,
    ConfirmationService,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'reset, tailwind-base, primeng-theme, primeng, checkups-base, tailwind-utilities, checkups, checkups-utilities',
          },
          inlineStyles: false,
        },
      },
    }),
    { provide: LOCALE_ID, useValue: locale },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'dd/MM/yyyy HH:mm:ss' } },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideTranslateService(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      MsalModule.forRoot(
        msalInstance,
        {
          interactionType: InteractionType.Redirect,
          authRequest: {
            scopes: ['user.read'],
          },
        },
        {
          interactionType: InteractionType.Redirect,
          protectedResourceMap: new Map([['https://graph.microsoft.com/v1.0/me', ['user.read']]]),
        },
      ),
    ),
    DecimalPipe,
    /*{
      provide: NG_SCROLLBAR_OPTIONS,
      useValue: {
        visibility: 'hover', // 'always' | 'hover' | 'native' | 'hidden'
        appearance: 'standard', // o 'compact'
        trackClass: 'custom-scroll-track',
        thumbClass: 'custom-scroll-thumbnail',
        autoHeightDisabled: true,
      },
    },*/
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    // DESACTIVADO: MsalNavigationClient navega ANTES de LOGIN_SUCCESS, causando que guards bloqueen
    // Ahora manejamos la navegación manualmente en auth.service después de cargar datos del usuario
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initializeMsalNavigationClient,
    //   deps: [Router],
    //   multi: true,
    // },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DateTransformInterceptor, multi: true },
    // Recupera de fallos de carga de chunks lazy tras un deploy (recarga una vez para tomar el bundle nuevo).
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    provideAppInitializer(() => {
      const featureFlagsService = inject(FeatureFlagsService);
      return featureFlagsService.load();
    }),
    // Bootstrap the ngx-translate loader independently of the header. Historically the header's
    // constructor was the only caller, so any state that hides the header — e.g. a residual
    // auth_type='jwt' making the app-root @if skip <app-header> — left the anonymous solar embed with
    // no loader and no missing-translation handler, rendering raw keys. Configuring it here guarantees
    // translations load on every route regardless of the header. setTranslateCurrentLoader's lastLang
    // guard makes the header's own call (and the per-user reload) idempotent.
    provideAppInitializer(() => {
      inject(TextsService).setTranslateCurrentLoader();
    }),
  ],
};

export function validateRoutes(routes: Routes): void {
  routes.forEach(route => {
    if (route.canActivate?.includes(AccessVerifierGuard) && !route.data) {
      throw new Error(`Route '${route.path}' is missing required 'data' attribute`);
    }
    if (route.children) {
      validateRoutes(route.children);
    }
  });
}
